'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Save, X } from 'lucide-react'
import type { CharacterResult } from '@creanizer/types'
import { BasicInfoEditor } from './editors/BasicInfoEditor'
import { PersonalityEditor } from './editors/PersonalityEditor'
import { BackgroundEditor } from './editors/BackgroundEditor'
import { AppearanceEditor } from './editors/AppearanceEditor'
import { BehaviorEditor } from './editors/BehaviorEditor'
import { DevelopmentEditor } from './editors/DevelopmentEditor'
import { SampleDialoguesEditor } from './editors/SampleDialoguesEditor'
import { MiscEditor } from './editors/MiscEditor'
import { EmotionalExpressionEditor } from './editors/EmotionalExpressionEditor'
import { InterpersonalPatternsEditor } from './editors/InterpersonalPatternsEditor'
import { ThinkingProcessEditor } from './editors/ThinkingProcessEditor'
import { DailyLifeEditor } from './editors/DailyLifeEditor'
import { BeliefsEditor } from './editors/BeliefsEditor'
import { ExpertiseEditor } from './editors/ExpertiseEditor'
import { RelationshipDetailsEditor } from './editors/RelationshipDetailsEditor'

interface CharacterEditModalEnhancedProps {
  isOpen: boolean
  onClose: () => void
  onSave: (updatedResult: CharacterResult) => void
  characterResult: CharacterResult
  title?: string
}

export function CharacterEditModalEnhanced({
  isOpen,
  onClose,
  onSave,
  characterResult,
  title = '詳細編集'
}: CharacterEditModalEnhancedProps) {
  const [editedResult, setEditedResult] = useState<CharacterResult>(characterResult)
  const [activeTab, setActiveTab] = useState('basic')

  useEffect(() => {
    setEditedResult(characterResult)
  }, [characterResult])

  const handleSave = () => {
    onSave(editedResult)
    onClose()
  }

  const handleCancel = () => {
    setEditedResult(characterResult)
    onClose()
  }

  const updateBasicInfo = (updates: Partial<CharacterResult>) => {
    setEditedResult(prev => ({ ...prev, ...updates }))
  }

  const updatePersonality = (personality: CharacterResult['personality']) => {
    setEditedResult(prev => ({ ...prev, personality }))
  }

  const updateBackground = (background: CharacterResult['background']) => {
    setEditedResult(prev => ({ ...prev, background }))
  }

  const updateAppearance = (appearance: CharacterResult['appearance']) => {
    setEditedResult(prev => ({ ...prev, appearance }))
  }

  const updateBehavior = (behavior: CharacterResult['behavior']) => {
    setEditedResult(prev => ({ ...prev, behavior }))
  }

  const updateDevelopment = (development: CharacterResult['development']) => {
    setEditedResult(prev => ({ ...prev, development }))
  }

  const updateSampleDialogues = (sampleDialogues: CharacterResult['sampleDialogues']) => {
    setEditedResult(prev => ({ ...prev, sampleDialogues }))
  }

  const updateMisc = (updates: { authorNotes?: string[], plotHooks?: string[], customContext?: string }) => {
    setEditedResult(prev => ({ ...prev, ...updates }))
  }

  const updateEmotionalExpression = (emotionalExpression: CharacterResult['emotionalExpression']) => {
    setEditedResult(prev => ({ ...prev, emotionalExpression }))
  }

  const updateInterpersonalPatterns = (interpersonalPatterns: CharacterResult['interpersonalPatterns']) => {
    setEditedResult(prev => ({ ...prev, interpersonalPatterns }))
  }

  const updateThinkingProcess = (thinkingProcess: CharacterResult['thinkingProcess']) => {
    setEditedResult(prev => ({ ...prev, thinkingProcess }))
  }

  const updateDailyLife = (dailyLife: CharacterResult['dailyLife']) => {
    setEditedResult(prev => ({ ...prev, dailyLife }))
  }

  const updateBeliefs = (beliefs: CharacterResult['beliefs']) => {
    setEditedResult(prev => ({ ...prev, beliefs }))
  }

  const updateExpertise = (expertise: CharacterResult['expertise']) => {
    setEditedResult(prev => ({ ...prev, expertise }))
  }

  const updateRelationshipDetails = (relationshipDetails: CharacterResult['relationshipDetails']) => {
    setEditedResult(prev => ({ ...prev, relationshipDetails }))
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap gap-1 h-auto p-1">
            <TabsTrigger value="basic" className="text-xs px-2">基本</TabsTrigger>
            <TabsTrigger value="personality" className="text-xs px-2">性格</TabsTrigger>
            <TabsTrigger value="background" className="text-xs px-2">背景</TabsTrigger>
            <TabsTrigger value="appearance" className="text-xs px-2">外見</TabsTrigger>
            <TabsTrigger value="behavior" className="text-xs px-2">行動</TabsTrigger>
            <TabsTrigger value="development" className="text-xs px-2">成長</TabsTrigger>
            <TabsTrigger value="emotional" className="text-xs px-2">感情</TabsTrigger>
            <TabsTrigger value="interpersonal" className="text-xs px-2">対人</TabsTrigger>
            <TabsTrigger value="thinking" className="text-xs px-2">思考</TabsTrigger>
            <TabsTrigger value="daily" className="text-xs px-2">日常</TabsTrigger>
            <TabsTrigger value="beliefs" className="text-xs px-2">信念</TabsTrigger>
            <TabsTrigger value="expertise" className="text-xs px-2">専門</TabsTrigger>
            <TabsTrigger value="relations" className="text-xs px-2">関係</TabsTrigger>
            <TabsTrigger value="dialogues" className="text-xs px-2">台詞</TabsTrigger>
            <TabsTrigger value="misc" className="text-xs px-2">その他</TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="basic">
              <BasicInfoEditor
                name={editedResult.name}
                age={editedResult.age}
                gender={editedResult.gender}
                role={editedResult.role}
                summary={editedResult.summary}
                onChange={updateBasicInfo}
              />
            </TabsContent>

            <TabsContent value="personality">
              <PersonalityEditor
                personality={editedResult.personality}
                onChange={updatePersonality}
              />
            </TabsContent>

            <TabsContent value="background">
              <BackgroundEditor
                background={editedResult.background}
                onChange={updateBackground}
              />
            </TabsContent>

            <TabsContent value="appearance">
              <AppearanceEditor
                appearance={editedResult.appearance}
                onChange={updateAppearance}
              />
            </TabsContent>

            <TabsContent value="behavior">
              <BehaviorEditor
                behavior={editedResult.behavior}
                onChange={updateBehavior}
              />
            </TabsContent>

            <TabsContent value="development">
              <DevelopmentEditor
                development={editedResult.development}
                onChange={updateDevelopment}
              />
            </TabsContent>

            <TabsContent value="emotional">
              {editedResult.emotionalExpression && (
                <EmotionalExpressionEditor
                  emotionalExpression={editedResult.emotionalExpression}
                  onChange={updateEmotionalExpression}
                />
              )}
              {!editedResult.emotionalExpression && (
                <p className="text-muted-foreground text-center py-8">
                  感情表現データがありません。再生成してください。
                </p>
              )}
            </TabsContent>

            <TabsContent value="interpersonal">
              {editedResult.interpersonalPatterns && (
                <InterpersonalPatternsEditor
                  interpersonalPatterns={editedResult.interpersonalPatterns}
                  onChange={updateInterpersonalPatterns}
                />
              )}
              {!editedResult.interpersonalPatterns && (
                <p className="text-muted-foreground text-center py-8">
                  対人関係データがありません。再生成してください。
                </p>
              )}
            </TabsContent>

            <TabsContent value="thinking">
              {editedResult.thinkingProcess && (
                <ThinkingProcessEditor
                  thinkingProcess={editedResult.thinkingProcess}
                  onChange={updateThinkingProcess}
                />
              )}
              {!editedResult.thinkingProcess && (
                <p className="text-muted-foreground text-center py-8">
                  思考プロセスデータがありません。再生成してください。
                </p>
              )}
            </TabsContent>

            <TabsContent value="daily">
              {editedResult.dailyLife && (
                <DailyLifeEditor
                  dailyLife={editedResult.dailyLife}
                  onChange={updateDailyLife}
                />
              )}
              {!editedResult.dailyLife && (
                <p className="text-muted-foreground text-center py-8">
                  日常生活データがありません。再生成してください。
                </p>
              )}
            </TabsContent>

            <TabsContent value="beliefs">
              {editedResult.beliefs && (
                <BeliefsEditor
                  beliefs={editedResult.beliefs}
                  onChange={updateBeliefs}
                />
              )}
              {!editedResult.beliefs && (
                <p className="text-muted-foreground text-center py-8">
                  信念データがありません。再生成してください。
                </p>
              )}
            </TabsContent>

            <TabsContent value="expertise">
              {editedResult.expertise && (
                <ExpertiseEditor
                  expertise={editedResult.expertise}
                  onChange={updateExpertise}
                />
              )}
              {!editedResult.expertise && (
                <p className="text-muted-foreground text-center py-8">
                  専門性データがありません。再生成してください。
                </p>
              )}
            </TabsContent>

            <TabsContent value="relations">
              {editedResult.relationshipDetails && (
                <RelationshipDetailsEditor
                  relationshipDetails={editedResult.relationshipDetails}
                  onChange={updateRelationshipDetails}
                />
              )}
              {!editedResult.relationshipDetails && (
                <p className="text-muted-foreground text-center py-8">
                  関係性詳細データがありません。再生成してください。
                </p>
              )}
            </TabsContent>

            <TabsContent value="dialogues">
              <SampleDialoguesEditor
                dialogues={editedResult.sampleDialogues}
                onChange={updateSampleDialogues}
              />
            </TabsContent>

            <TabsContent value="misc">
              <MiscEditor
                authorNotes={editedResult.authorNotes}
                plotHooks={editedResult.plotHooks}
                customContext={editedResult.customContext}
                onChange={updateMisc}
              />
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            キャンセル
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}